import { inject, Injectable } from '@angular/core';
import { AbstractApiStore } from '@kehe/phoenix-component-store-utils';
import { TransactionTypeService } from '../services/transaction-type.service';
import { switchMap, tap } from 'rxjs/operators';
import { tapResponse } from '@ngrx/operators';
import { ApiFailureText } from '@app/enum/alert-text.enum';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class DownloadTemplateApiStore extends AbstractApiStore<string> {
  private readonly _transactionTypesService = inject(TransactionTypeService);

  downloadTemplate$ = this.effect(
    (
      params$: Observable<{
        transactionTypeName: string;
        isEsnTemplate: boolean;
      }>,
    ) =>
      params$.pipe(
        tap(() => {
          this.startLoading();
        }),
        switchMap(({ transactionTypeName, isEsnTemplate }) => {
          const name =
            transactionTypeName?.length > 0 ? transactionTypeName : '--';

          return this._transactionTypesService
            .getPreSignedTemplateURL(name, isEsnTemplate)
            .pipe(
              tapResponse({
                next: (response) => {
                  this.handleFileDownload(response, transactionTypeName);
                },
                error: () => {
                  this.setError(ApiFailureText.Generic);
                },
              }),
            );
        }),
      ),
  );
  readonly loadTemplateFile = this.effect<{
    code: string;
    onLoaded: (file: File) => void;
    onError?: (e: any) => void;
  }>((req$) =>
    req$.pipe(
      tap(() => this.patchState({ isLoading: true, apiError: null })),
      switchMap(({ code, onLoaded, onError }) =>
        this._transactionTypesService.getPreSignedTemplateURL(code).pipe(
          switchMap((url) =>
            fetch(url).then(async (res) => {
              if (!res.ok)
                throw new Error(`Failed to download template (${res.status})`);

              const blob = await res.blob();

              const nameFromUrl = decodeURIComponent(
                url.split('?')[0].split('/').pop() ?? 'template.xlsx',
              );

              const file = new File([blob], nameFromUrl, {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              });

              return file;
            }),
          ),
          tap({
            next: (file) => {
              this.patchState({ isLoading: false });
              onLoaded(file);
            },
            error: (e) => {
              this.patchState({ isLoading: false, apiError: e });
              onError?.(e);
            },
          }),
        ),
      ),
    ),
  );

  readonly loadSupportingDocument = this.effect<{
    code: string;
    onLoaded: (file: File) => void;
    onError?: (e: any) => void;
  }>((req$) =>
    req$.pipe(
      tap(() => this.patchState({ isLoading: true, apiError: null })),
      switchMap(({ code, onLoaded, onError }) =>
        this._transactionTypesService
          .getPreSignedSupportingDocumentURL(code)
          .pipe(
            switchMap((url) =>
              fetch(url).then(async (res) => {
                if (!res.ok)
                  throw new Error(
                    `Failed to download template (${res.status})`,
                  );

                const blob = await res.blob();

                const nameFromUrl = decodeURIComponent(
                  url.split('?')[0].split('/').pop() ??
                    'supporting_Document.pdf',
                );

                const file = new File([blob], nameFromUrl, {
                  type: 'application/pdf',
                });

                return file;
              }),
            ),
            tap({
              next: (file) => {
                this.patchState({ isLoading: false });
                onLoaded(file);
              },
              error: (e) => {
                this.patchState({ isLoading: false, apiError: e });
                onError?.(e);
              },
            }),
          ),
      ),
    ),
  );

  private handleFileDownload(url: string, fileName: string) {
    fetch(url)
      .then((res) => res.blob())
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        if (fileName?.length > 0) {
          a.download = `${fileName}.xlsx`;
        } else {
          a.download = 'basetemplate.xlsx';
        }
        a.click();
        URL.revokeObjectURL(a.href);
      });
  }
}
